import {
  useEndCbtMonitoringSessionMutation,
  useStartCbtMonitoringSessionMutation,
} from '@/types/vue-apollo-helpers';
import { useAntiCheatDetection } from '../composables/useAntiCheatDetection';
import { useRecordConsent } from '../composables/useRecordConsent';
import { useSignalingToken } from '../composables/useSignalingToken';
import { startMediasoupProducer, type ProducerHandle } from '../composables/useMediasoupProducer';

export interface StudentMonitoringHandle {
  monitoringSessionId: string;
  producer: ProducerHandle;
  stop: () => void;
}

/**
 * Student-side hook. Call once the student has explicitly accepted the
 * monitoring consent (e.g. via `MonitoringConsentBanner`) and the CBT test
 * session has been created. Starts, in order:
 *   1. Records the student's consent (required by the backend before a
 *      monitoring session can be created)
 *   2. Creates the monitoring session
 *   3. Issues a short-lived signaling JWT and connects to the MediaSoup SFU
 *      as a producer
 *   4. Publishes the webcam track
 *   5. Attaches anti-cheat DOM listeners
 *
 * Returns a handle. Call `handle.stop()` on exam submit / unmount; that
 * tears down the producer and detaches all DOM listeners.
 *
 * Usage (in CBT.vue or wherever the exam starts, AFTER the student clicks
 * "I understand" on `MonitoringConsentBanner`):
 *
 *   const monitoring = useStudentMonitoring();
 *   const handle = await monitoring.start(testSessionId);
 *   // …on submit:
 *   handle.stop();
 */
export function useStudentMonitoring() {
  const { mutate: startSession } = useStartCbtMonitoringSessionMutation();
  const { mutate: endSession } = useEndCbtMonitoringSessionMutation();
  const { record: recordConsent } = useRecordConsent();
  const { issue } = useSignalingToken();

  const start = async(cbtTestSessionId: string): Promise<StudentMonitoringHandle> => {
    // Consent must land before startCbtMonitoringSession — the backend
    // hard-fails session creation without an existing consent row for
    // this (student, test_session) pair.
    await recordConsent(cbtTestSessionId);

    const result = await startSession({ cbt_test_session_id: cbtTestSessionId });
    const session = result?.data?.startCbtMonitoringSession;
    if (!session) throw new Error('Failed to start monitoring session');

    const tokenInfo = await issue(session.id, 'producer');
    const producer = await startMediasoupProducer({
      wsUrl: tokenInfo.ws_url,
      token: tokenInfo.token,
    });
    await producer.publishWebcam();

    const detection = useAntiCheatDetection(session.id, {
      webcamStream: () => producer.localStream(),
    });
    detection.attach();

    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;

      detection.detach();
      producer.stop();

      // Tell the backend the session has ended. Fire-and-forget — if this
      // fails (e.g. network loss as the page unloads), the producer's
      // transportclose on the SFU side still finalizes any recording, and
      // the admin dashboard will show the session as stale rather than
      // truly active. We don't await because stop() runs in onUnmounted /
      // pagehide where async work isn't guaranteed to complete.
      endSession({ id: session.id }).catch(() => { /* best-effort */ });
    };

    return {
      monitoringSessionId: session.id,
      producer,
      stop,
    };
  };

  return { start };
}
